import React, { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Link } from 'react-router-dom';
import { Typography, IconButton, Tooltip, Button } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import DeviceIcon from '#component/icon/device-icon';
import HighlightWord from '#component/general/highlight-word/highlight-word';
import { getHighlightedSearchResult } from '#component/general/highlight-word/helper';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  LabelContainer,
  WarehouseLabel,
  ProjectLabel,
  SiteCardButtonContainer,
  AddAppCardButton,
  TooltipWrapper,
  StyledChip,
  ChipLabelContainer,
  BellIcon,
  ButtonContainer
} from './styles';

const MAX_CHIPS = 7;

const LabelComponent = props => {
  const { label, devices = [] } = props;
  const alerts = useMemo(() => devices.filter(d => d.projectAlertId), [devices]);
  const labelComponent =
    <ChipLabelContainer>
      <span>{label}</span>
      <span>
        <DeviceIcon />
        <span aria-label="Appliance devices">{devices.length}</span>
        <BellIcon />
        <span aria-label="Appliance alerts">{alerts.length ? alerts.length : 0}</span>
      </span>
    </ChipLabelContainer>;

  return labelComponent;
};

const WarehouseAccordion = props => {
  const {
    warehouse,
    setWarehouse,
    userType,
    showAllIndex,
    expanded,
    setShowAllIndex,
    setAnchorEl
  } = props;
  const showAll = showAllIndex === warehouse.id;
  const listFilter = useSelector(s => s.listFilter, shallowEqual) ?? {};
  const highlightedSearchResult = getHighlightedSearchResult(listFilter);

  const toggleShowAll = () => {
    if (showAll) {
      localStorage.removeItem('showAllAppliances');
      setShowAllIndex(false);
    } else {
      localStorage.setItem('showAllAppliances', warehouse.id);
      setShowAllIndex(warehouse.id);
    }
  };

  const handleWarehouseClick = wh => e => {
    e.stopPropagation();
    setWarehouse(wh);
    setAnchorEl(e.currentTarget);
  };

  return (
    <StyledAccordion
      key={warehouse.id}
      expanded={expanded}
      square
    >
      <StyledAccordionSummary>
        <LabelContainer>
          <WarehouseLabel>
            {highlightedSearchResult ? <HighlightWord
              textToHighlight={warehouse.label}
              searchWord={highlightedSearchResult}
            /> : warehouse.label}
          </WarehouseLabel>
          <ProjectLabel>
            {warehouse.project?.title}
          </ProjectLabel>
        </LabelContainer>

        {userType < 10 &&
          <SiteCardButtonContainer>
            <AddAppCardButton
              variant="contained"
              color="secondary"
              component={Link}
              to={`/warehouse/appliance/create/${warehouse?.id}`}
            >
              Add application
            </AddAppCardButton>
            <TooltipWrapper>
              <Tooltip title="Site actions">
                <IconButton size="small" onClick={handleWarehouseClick(warehouse)}>
                  <ArrowDropDown />
                </IconButton>
              </Tooltip>
            </TooltipWrapper>
          </SiteCardButtonContainer>}
      </StyledAccordionSummary>
      <StyledAccordionDetails isEmpty={!warehouse.appliances?.length}>
        {
          warehouse.appliances?.slice(0, showAll ?
            undefined : MAX_CHIPS).map(({ id, label, applianceDevices }) =>
              <StyledChip
                key={id}
                variant="outlined"
                clickable
                component={Link}
                to={`/warehouse/appliance/edit/${id}`}
                label={
                  <LabelComponent
                    label={label}
                    devices={applianceDevices}
                  />
                }
              />)
        }
        {warehouse.appliances?.length > MAX_CHIPS &&
          <ButtonContainer>
            <Button onClick={toggleShowAll}>
              {!showAll ? `Show all (${warehouse.appliances?.length})` : 'Show less'}
            </Button>
          </ButtonContainer>}
        {!warehouse.appliances?.length && (
          <Typography color="textSecondary" variant="h5">
            No appliances
          </Typography>)}
      </StyledAccordionDetails>
    </StyledAccordion>);
};

export default WarehouseAccordion;
